import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { linkedRecord, opensAsPage, opensAsSheet, recordPage, recordSection, RECORD_KINDS } from './open-in';
import { opensQuickViews } from './quick-view-pages';

/**
 * A plain click on a link to a task, a deal or a contact, wherever the link is (TF-367): it opens the
 * record the way the person chose in My profile. A link to its page opens the side sheet when they
 * chose "Side sheet"; a link that opens the sheet (?task=<id>) opens the page when they chose "Full
 * page". Mounted once in the shell; nothing changes until a choice is made.
 *
 * Left as they are: a click with a modifier or another button (the browser opens the page in a new
 * tab), a link to another company, the sheet's own "Open as page", the record's own section, whose
 * list and board decide for themselves (CrmListDetail, the quick-view hooks), and a deal or a contact
 * clicked on a page without its sheet, such as the files page (TF-385): it opens as a page there.
 */
export function RecordLinkChoice() {
  const location = useLocation();
  const navigate = useNavigate();
  // Read when a click comes, so the listener is added once.
  const current = useRef(location);
  current.current = location;

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(anchor instanceof HTMLAnchorElement) || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      if (anchor.closest('.sheet-page-link')) return;
      const url = new URL(anchor.href);
      if (url.origin !== window.location.origin) return;
      const record = linkedRecord(url.pathname, url.search);
      const here = current.current;
      if (!record || !here.pathname.startsWith(`/c/${record.tenantId}/`)) return;

      if (record.inSheet && opensAsPage(record.kind)) {
        event.preventDefault();
        navigate(recordPage(record.tenantId, record.kind, record.id));
      } else if (
        !record.inSheet &&
        opensAsSheet(record.kind) &&
        !here.pathname.startsWith(recordSection(record.tenantId, record.kind)) &&
        (record.kind === 'task' || opensQuickViews(here.pathname))
      ) {
        event.preventDefault();
        // Over the page it was clicked on; one sheet at a time, and back closes it (the hooks' close).
        const next = new URLSearchParams(here.search);
        for (const kind of RECORD_KINDS) next.delete(kind);
        next.set(record.kind, record.id);
        navigate({ pathname: here.pathname, search: `?${next}` }, { state: { [`${record.kind}QuickView`]: true } });
      }
    };
    // Before the router's own handler on the link, which then sees the click as handled.
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [navigate]);

  return null;
}
